import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, RefreshCw, Send } from 'lucide-react';
import '../components/aiinterview/aiinterview.css';
import { aiInterviewsAPI, errMsg } from '../lib/aiInterviewApi';
import CompetencyEditor, { toImportance } from '../components/aiinterview/recruiter/CompetencyEditor';
import InviteDialog from '../components/aiinterview/recruiter/InviteDialog';
import { InterviewTable } from '../components/aiinterview/recruiter/InterviewsBoard';
import { Loading, Pill, PROCTORING, Section } from '../components/aiinterview/recruiter/common';
import { Switch } from '../components/ui/switch';

const fromPlan = (p) => ({
  name: p.name || '', duration_min: p.duration_min || 30, language: p.language || 'en', role_level: p.role_level || '',
  focus: (p.focus_areas || []).join(', '), proctoring_level: p.proctoring_level || 'standard', auto_invite_on_stage: !!p.auto_invite_on_stage,
});
const compsFrom = (list) => list.map((c) => ({ ...c, importance: toImportance(c.weight, list.length) }));

const L = ({ label, children }) => <label className="iv-small" style={{ display: 'grid', gap: '0.3rem', fontWeight: 700, color: '#002A38' }}>{label}{children}</label>;

function ConfigForm({ f, set }) {
  const on = (k) => (e) => set({ ...f, [k]: e.target.value });
  return (
    <div style={{ display: 'grid', gap: '0.9rem', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
      <L label="Plan name"><input className="iv-input" value={f.name} onChange={on('name')} data-testid="plan-name-input" /></L>
      <L label="Duration">
        <select className="iv-select" value={f.duration_min} onChange={on('duration_min')} data-testid="plan-duration-select">
          {[15, 30, 45].concat([15, 30, 45].includes(Number(f.duration_min)) ? [] : [Number(f.duration_min)]).map((m) => <option key={m} value={m}>{m} minutes</option>)}
        </select>
      </L>
      <L label="Language">
        <select className="iv-select" value={f.language} onChange={on('language')} data-testid="plan-language-select"><option value="en">English</option><option value="hi">Hindi</option></select>
      </L>
      <L label="Role level">
        <select className="iv-select" value={f.role_level} onChange={on('role_level')} data-testid="plan-level-select">
          <option value="">Infer from JD</option>{['junior', 'mid', 'senior', 'lead'].map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
      </L>
      <L label="Proctoring">
        <select className="iv-select" value={f.proctoring_level} onChange={on('proctoring_level')} data-testid="plan-proctoring-select">
          {Object.entries(PROCTORING).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </L>
      <L label="Focus areas (comma separated)"><input className="iv-input" value={f.focus} onChange={on('focus')} data-testid="plan-focus-input" /></L>
      <label className="iv-row iv-small" style={{ gap: '0.5rem', gridColumn: '1 / -1' }}>
        <Switch checked={f.auto_invite_on_stage} onCheckedChange={(v) => set({ ...f, auto_invite_on_stage: v })} data-testid="plan-auto-invite-switch" />
        Automatically invite candidates when they move to the Interview stage of the pipeline
      </label>
    </div>
  );
}

export default function AIInterviewPlanPage() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [f, setF] = useState(null);
  const [comps, setComps] = useState([]);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  const load = useCallback(async () => {
    const { data } = await aiInterviewsAPI.plan(planId);
    setPlan(data);
    setF((cur) => cur || fromPlan(data));
    if (data.status !== 'generating') setComps((cur) => (cur.length ? cur : compsFrom(data.competencies)));
    return data;
  }, [planId]);

  useEffect(() => {
    let t;
    const poll = () => load().then((d) => { if (d.status === 'generating') t = setTimeout(poll, 4000); }).catch(() => toast.error('Plan not found'));
    poll();
    aiInterviewsAPI.list({ plan_id: planId }).then(({ data }) => setRows(data)).catch(() => {});
    return () => clearTimeout(t);
  }, [load, planId]);

  const save = async () => {
    if (comps.some((c) => c.name.trim().length < 2)) { toast.error('Every competency needs a name'); return; }
    setBusy(true);
    try {
      const { data } = await aiInterviewsAPI.updatePlan(planId, {
        name: f.name, duration_min: Number(f.duration_min), language: f.language, role_level: f.role_level, proctoring_level: f.proctoring_level,
        focus_areas: f.focus.split(',').map((x) => x.trim()).filter(Boolean), auto_invite_on_stage: f.auto_invite_on_stage,
        competencies: comps.map((c) => ({ name: c.name.trim(), type: c.type, weight: c.importance / 5, must_have: !!c.must_have, evidence_needed: c.evidence_needed || '' })),
      });
      setPlan(data);
      setComps(compsFrom(data.competencies));
      toast.success(`Plan saved (version ${data.version})`);
    } catch (e) {
      toast.error(errMsg(e, 'Could not save the plan'));
    } finally {
      setBusy(false);
    }
  };
  const regenerate = async () => {
    await aiInterviewsAPI.regeneratePlan(planId);
    setComps([]);
    const poll = () => load().then((d) => { if (d.status === 'generating') setTimeout(poll, 4000); });
    poll();
  };

  if (!plan || !f) return <div className="iv iv-rpage"><Loading /></div>;
  const generating = plan.status === 'generating';
  return (
    <div className="iv iv-rpage" data-testid="ai-interview-plan-page">
      <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" onClick={() => navigate('/interviews?tab=plans')} data-testid="plan-back-btn"><ArrowLeft size={14} /> All plans</button>
      <div className="iv-rhead" style={{ marginTop: '0.9rem' }}>
        <div>
          <div className="iv-eyebrow">{plan.job_title}</div>
          <h1 className="iv-rtitle" data-testid="plan-title">{plan.name}</h1>
          <div className="iv-row" style={{ marginTop: '0.35rem' }}>
            <Pill tone={generating ? 'teal' : plan.status === 'ready' ? 'green' : 'red'} testId="plan-status">{generating ? 'AI is drafting competencies…' : plan.status === 'ready' ? 'Ready' : 'Generation failed'}</Pill>
            <span className="iv-small">Version {plan.version}</span>
          </div>
        </div>
        <div className="iv-row">
          <button type="button" className="iv-btn iv-btn-ghost" disabled={generating} onClick={regenerate} data-testid="plan-regenerate-btn"><RefreshCw size={15} /> Regenerate with AI</button>
          <button type="button" className="iv-btn iv-btn-cta" disabled={plan.status !== 'ready'} onClick={() => setInviteOpen(true)} data-testid="plan-invite-btn"><Send size={15} /> Invite candidates</button>
        </div>
      </div>
      {plan.error && <div className="iv-note is-bad" style={{ marginBottom: '1rem' }}>{plan.error}</div>}
      <Section title="Interview settings" testId="plan-config"><ConfigForm f={f} set={setF} /></Section>
      <Section title="Competencies & weighting" testId="plan-competencies"
        right={<button type="button" className="iv-btn iv-btn-primary iv-btn-sm" disabled={busy || generating || !comps.length} onClick={save} data-testid="plan-save-btn">{busy ? 'Saving…' : 'Save plan'}</button>}>
        <p className="iv-small" style={{ marginBottom: '0.75rem' }}>Every candidate for this role is scored on the same job-related competencies (1–5 anchors). Must-haves that score below the bar cap the advisory recommendation.</p>
        {generating ? <Loading label="Drafting competencies from the job description…" /> : <CompetencyEditor comps={comps} setComps={setComps} />}
      </Section>
      <Section title={`Interviews on this plan (${rows.length})`} testId="plan-interviews">
        {rows.length ? <InterviewTable rows={rows} testId="plan-interviews-table" /> : <p className="iv-small">No candidates invited yet.</p>}
      </Section>
      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} presetPlan={plan} onSent={() => aiInterviewsAPI.list({ plan_id: planId }).then(({ data }) => setRows(data))} />
    </div>
  );
}
